import { useState, useEffect, useCallback } from "react";
import Grid from "../components/grid";
import Container from "../components/basicComponents/container";
import { removeNews, editNews } from "../state-management/actions/news-actions";
import { useDispatch, useSelector } from "react-redux";
import {
  setAlert,
  setAlertSeverity,
  setAlertContext,
} from "../state-management/actions/alerts-actions";
import { selectGlobalState } from "../state-management/selectors";
import { NewsDataProps } from "../types/public";
import NewsComponent from "../components/news";
import { setRowStatus } from "../state-management/actions/global-state-actions";
import { getNews, getNewsById } from "../state-management/actions/news-actions";
import { createNotificationData } from "../utils/createNotificationData";
import { addNotification } from "../state-management/actions/notification-actions";

const initialData = {
  title: "",
  department_id: "",
  content: "",
  index_picture: "",
  tags: "",
  is_active: false,
  description: "",
};

export default function NewsList() {
  const dispatch = useDispatch();
  const [editFlag, setEditFlag] = useState(false);
  const { departmentsData, currentUserData, usersData, groupingData } =
    useSelector(selectGlobalState);

  const [formData, setFormData] = useState<NewsDataProps>({
    ...initialData,
    created_by: currentUserData._id,
  });
  const [newsData, setNewsData] = useState<any>([]);
  const [loading, setLoading] = useState(false);

  const permissionDepartment = groupingData.find(
    (item: any) => item.user_id === currentUserData._id
  )?.department_id || ["none"];

  const fetchData = useCallback(async () => {
    try {
      const response = await getNews()(dispatch);
      if (response.data) {
        const filterNewsData = response.data
          .filter((news: any) =>
            permissionDepartment.includes(news.department_id)
          )
          .sort((a: any, b: any) => {
            const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
            const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
            return dateB - dateA;
          });
        setNewsData(filterNewsData);
      }
    } catch (error) {
      console.log(error);
    }
  }, [dispatch]);

  useEffect(() => {
    dispatch(setRowStatus("clear id", false));
    fetchData();
  }, []);

  const handleEdit = async (values: NewsDataProps) => {
    try {
      setLoading(true);
      const { news_id, ...cleanData } = values;
      const response = await editNews(news_id as string, cleanData)(dispatch);
      if (response.data && response.status === 200) {
        const editData = { ...cleanData, _id: news_id };
        setNewsData((prev: any) =>
          prev.map((news: any) =>
            news._id === formData.news_id ? { ...news, ...editData } : news
          )
        );
        dispatch(setRowStatus("clear id", false));
        dispatch(setAlert(true));
        dispatch(setAlertSeverity("success"));
        dispatch(setAlertContext("ویرایش خبر با موفقیت صورت پذیرفت"));
        setFormData({
          ...initialData,
          created_by: currentUserData._id,
        });
        setEditFlag(false);
        setLoading(false);
        const notificationData = createNotificationData({
          data: cleanData,
          departmentsData,
          currentUserData,
          groupingData,
          type: "edit",
          subject: "خبر",
        });
        await addNotification(notificationData)(dispatch);
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const handleSetData = async (clear: boolean, id: any) => {
    setEditFlag(clear ? false : true);
    try {
      const editData = newsData?.find((news: any) => news._id === id);
      const response = await getNewsById(id)(dispatch);
      if (editData && response.data && !clear) {
        const { _id, ...data } = editData as NewsDataProps;
        setFormData({
          ...data,
          news_id: _id,
          content: response.data.content,
          index_picture: response.data.index_picture,
        });
      }
      if (clear) {
        setFormData({
          ...formData,
          content: "",
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const response = await removeNews(id)(dispatch);
      if (response.data) {
        setNewsData(newsData.filter((news: any) => news._id !== id));
        dispatch(setAlert(true));
        dispatch(setAlertSeverity("success"));
        dispatch(setAlertContext(" خبر مربوطه حذف شد"));
        const notificationData = createNotificationData({
          data: newsData.find((item: any) => item._id === id),
          departmentsData,
          currentUserData,
          groupingData,
          type: "delete",
          subject: "خبر",
        });
        await addNotification(notificationData)(dispatch);
      }
    } catch (error) {
      console.log(error);
    }
  };
  const columns: any = [
    {
      headerName: "عنوان اخبار",
      field: "title",
      width: 500,
      sortable: true,
    },
    {
      headerName: "نویسنده",
      valueGetter: (params: any) => {
        const author = usersData.find(
          (user: any) => user._id === params.data.created_by
        );
        return author ? `${author.firstname} ${author.lastname}` : "";
      },
      sortable: true,
    },
    {
      headerName: "دسته بندی",
      valueGetter: (params: any) =>
        departmentsData.find(
          (dep: any) => dep._id === params.data.department_id
        )?.name || "",
      sortable: true,
    },
    { headerName: "تگ ها", field: "tags" },
    { headerName: "انتشار", field: "is_active" },
  ];

  return (
    <div className="flex flex-col items-center w-full">
      <Container header="لیست اخبار">
        <Grid
          height={700}
          columns={columns}
          userData={newsData}
          pageSize={10}
          editable={false}
          deleteBtn={true}
          editBtn={true}
          pagination={true}
          btnHeader="عملیات"
          handleSetData={handleSetData}
          handleDelete={handleDelete}
          handleEdit={handleEdit}
        />
      </Container>
      <div
        className="flex flex-col items-center w-full"
        style={{ display: editFlag ? "block" : "none" }}
      >
        <Container header="ویرایش">
          <NewsComponent
            formData={formData}
            setFormData={setFormData}
            handleSubmit={handleEdit}
            currentUserId={currentUserData._id}
            loading={loading}
          />
        </Container>
      </div>
    </div>
  );
}
